"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.CanvasWorkflow = void 0;
const bson_1 = require("bson");
const ajv_1 = __importDefault(require("ajv"));
const lib_1 = require("../lib");
const Types_1 = require("./Types");
class CanvasWorkflow {
  next_workflow_id;
  id;
  type;
  settings;
  outputPin;
  inputPin;
  customProcess;
  _schema;
  get schema() {
    return this._schema;
  }
  set schema(value) {
    this._schema = value;
    this.validated = this.validateSchema();
  }
  validated;
  lastValidationError = null;
  constructor() {
    this.validated = false;
  }
  setDefaultNextWorkflow(pin, next_workflow_id) {
    if (pin === Types_1.OutputPin.OUTPUT && this.outputPin.includes(pin)) {
      this.next_workflow_id = next_workflow_id;
    }
  }
  getCustomProcess(a) {
    return this.customProcess[a];
  }
  /**
   * Check if a given custom process is compatible
   * with the new process builder.
   * @returns `true` if compatible, `false` otherwise.
   */
  isBuilderCompatible(customProcessName) {
    const process = this.getCustomProcess(customProcessName);
    if (!process) {
      // When defining a custom process for the first time,
      // use the new process builder.
      return true;
    }
    if (!Array.isArray(process)) {
      return true;
    }
    return (0, lib_1.checkCompatibility)(process);
  }
  getDefaultNextWorkflow() {
    if (this.outputPin && this.next_workflow_id) {
      return this.next_workflow_id;
    }
    return null;
  }
  setWorkflow(c) {
    this.settings = {
      ...this.getDefaultSettings(),
      ...this.getFormattedWorkflowSettings(c.settings)
    };
    this.id = c.id;
    if (this.outputPin && c.settings.next_workflow_ids && c.settings.next_workflow_ids.next_workflow) {
      this.next_workflow_id = c.settings.next_workflow_ids.next_workflow;
    }
    this.validateSchema();
  }
  getFormattedWorkflowSettings(settings) {
    const defaultSettings = this.getDefaultSettings();
    // assign non-nested property that is null with default value
    // https://az2019.azavista.com/product-management/frontend/azavista3.0/-/issues/4295#note_109697
    const clonedSettings = {
      ...settings
    };
    Object.keys(clonedSettings).forEach(key => {
      if (clonedSettings[key] == null) {
        if (defaultSettings[key] !== undefined) {
          clonedSettings[key] = defaultSettings[key];
        } else {
          delete clonedSettings[key];
        }
      }
    });
    return clonedSettings;
  }
  getDefaultSettings() {
    return {};
  }
  isValidated() {
    return this.validated;
  }
  validateSchema() {
    const ajv = new ajv_1.default({
      allErrors: true
    });
    const validator = ajv.compile(this.schema);
    if (!validator(this.settings)) {
      this.lastValidationError = validator.errors;
      this.validated = false;
      return false;
    }
    this.validated = true;
    return true;
  }
  setValues(values) {
    this.settings = this.getFormattedWorkflowSettings({
      ...this.settings,
      ...values
    });
    return this.validateSchema();
  }
  getId() {
    if (!this.id) {
      this.id = new bson_1.ObjectId().toString();
    }
    return this.id;
  }
  getWorkflow() {
    const s = {
      ...this.settings
    };
    if (this.next_workflow_id !== undefined) {
      s.next_workflow_ids = {
        ...(s.next_workflow_ids || {}),
        next_workflow: this.next_workflow_id
      };
    }
    return {
      id: this.getId(),
      type: this.type,
      settings: s,
      custom_processes: this.customProcess
    };
  }
  getPossibleWorkflows(o, ws) {
    const r = [];
    const id = this.getId();
    if (!this.outputPin.includes(o)) {
      return [];
    }
    if (o === Types_1.OutputPin.OUTPUT || o === Types_1.OutputPin.NEW_CONTACT || o === Types_1.OutputPin.INVITED_CONTACT || o === Types_1.OutputPin.EXISTING_CONTACT || o === Types_1.OutputPin.SPLITTER_OUTPUT1 || o === Types_1.OutputPin.SPLITTER_OUTPUT2 || o === Types_1.OutputPin.APPROVAL_DENY_OUTPUT || o === Types_1.OutputPin.SUBPARTICIPANT_OUTPUT) {
      ws.forEach(w => {
        if (w.inputPin.includes(Types_1.InputPin.INPUT)) {
          const i = w.getId();
          if (i !== id) {
            r.push(i);
          }
        }
      });
    }
    if (o === Types_1.OutputPin.CANCELLATION_OUTPUT) {
      ws.forEach(w => {
        if (w.inputPin.includes(Types_1.InputPin.CANCELLATION_INPUT)) {
          const i = w.getId();
          if (i !== id) {
            r.push(i);
          }
        }
      });
    }
    if (o === Types_1.OutputPin.DECLINE_OUTPUT) {
      ws.forEach(w => {
        if (w.inputPin.includes(Types_1.InputPin.DECLINE_INPUT)) {
          const i = w.getId();
          if (i !== id) {
            r.push(i);
          }
        }
      });
    }
    return r;
  }
  getInputPin() {
    return this.inputPin;
  }
  getOutputPin() {
    return this.outputPin;
  }
  getProperties() {
    return [];
  }
  getProperty(property) {
    return this.findProperty(this.getProperties(), property);
  }
  findProperty(properties, name) {
    for (const p of properties) {
      if (p.attribute === name) {
        return p;
      }
    }
    return null;
  }
  getPropertiesWithSchema() {
    return this.getProperties().map(property => ({
      ...property,
      schema: this.schema.properties?.[property.attribute]
    }));
  }
  validateAttribute(a, value = undefined) {
    // validate processes
    if (a.type === 'process') {
      if (value === undefined) {
        if (this.customProcess[a.attribute] === undefined) {
          return {
            result: false,
            errormsg: ''
          };
        }
        value = this.customProcess[a.attribute];
      }
      if (a.mandatory && !value) {
        return {
          result: false,
          errormsg: ''
        };
      }
      return {
        result: true,
        errormsg: ''
      };
    }
    // validate normal settings attriburte
    if (value === undefined) {
      if (this.settings[a.attribute] === undefined) {
        return {
          result: false,
          errormsg: ''
        };
      }
      value = this.settings[a.attribute];
    }
    if (this.schema && this.schema.properties && this.schema.properties[a.attribute]) {
      const ajv = new ajv_1.default({
        allErrors: true
      });
      const validator = ajv.compile(this.schema.properties[a.attribute]);
      if (!validator(value)) {
        let e = '';
        if (validator.errors && validator.errors[0] && validator.errors[0].message) {
          e = validator.errors[0].message;
        }
        return {
          result: false,
          errormsg: e
        };
      }
    }
    if (!a.mandatory) {
      return {
        result: true,
        errormsg: ''
      };
    }
    let hasvalue = false;
    if (value) {
      hasvalue = true;
    }
    if (a.type === 'multiselect') {
      hasvalue = false;
      if (Array.isArray(value)) {
        if (value.length > 0) {
          hasvalue = true;
        }
      }
    }
    if (a.type === 'number') {
      hasvalue = false;
      if (value || value === 0) {
        hasvalue = true;
      }
    }
    if (a.type === 'boolean') {
      hasvalue = false;
      if (value === true || value === false) {
        hasvalue = true;
      }
    }
    if (a.type === 'advanced-query') {
      hasvalue = false;
      if (Array.isArray(value) && value.length > 0) {
        hasvalue = true;
      }
    }
    return {
      result: hasvalue,
      errormsg: ''
    };
  }
}
exports.CanvasWorkflow = CanvasWorkflow;
